<i18n>
  {
    "fr": {
      "ConfiguratorEditorThemePickerModal": {
        "title": "Choisir un visuel",
        "subTitle": "Sélectionnez un visuel pour voir le rendu",
        "confirm-action": "Valider ce visuel"
      }
    }
  }
</i18n>

<template>
  <div :class="blockClasses">
    <div :class="`${BLOCK_SELECTOR}__header`">
      <div :class="`${BLOCK_SELECTOR}__title-wrapper`">
        <h2 :class="`${BLOCK_SELECTOR}__title`">
          {{ $t('ConfiguratorEditorThemePickerModal.title') }}
        </h2>
        <p :class="`${BLOCK_SELECTOR}__sub-title`">
          {{ $t('ConfiguratorEditorThemePickerModal.subTitle') }}
        </p>
      </div>
      <button
        :class="`${BLOCK_SELECTOR}__close-button`"
        type="button"
        @click="$emit('close')"
      ></button>
    </div>

    <div :class="`${BLOCK_SELECTOR}__body`">
      <ConfiguratorCategoryDualSelect
        ref="dualSelect"
        v-model="activeCategoryId"
        :class="dualSelectClasses"
        :orientation="isDocked ? 'horizontal' : 'vertical'"
      />

      <ConfiguratorProductListVirtual
        ref="productListVirtual"
        :class="`${BLOCK_SELECTOR}__product-list-virtual`"
        :items="products"
        :theme-type="genericThemeType"
      />
    </div>

    <div
      v-if="themeType === ThemeType.GENERIC"
      :class="`${BLOCK_SELECTOR}__footer`"
    >
      <BtnToolbar :class="`${BLOCK_SELECTOR}__footer-toolbar`" centered>
        <BaseBtn
          :class="`${BLOCK_SELECTOR}__button-confirm`"
          secondary
          @click="handleConfirmAction"
          v-text="$t('ConfiguratorEditorThemePickerModal.confirm-action')"
        />
      </BtnToolbar>
    </div>
  </div>
</template>

<script>
import {
  ConfiguratorAction,
  mapState,
  prefixType as prefixConfiguratorType,
} from '../../../store/modules/configurator';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../../../store/modules/configurator/glasses';
import ConfiguratorCategoryDualSelect from '../ConfiguratorCategoryDualSelect';
import ConfiguratorProductListVirtual from '../ConfiguratorProductListVirtual';
import { ThemeType } from '../../../api/static/ThemeType';
import BtnToolbar from '../../BtnToolbar';
import BaseBtn from '../../BaseBtn';

export default {
  name: 'ConfiguratorEditorThemePickerModal',
  emits: ['close'],
  components: {
    BtnToolbar,
    BaseBtn,
    ConfiguratorCategoryDualSelect,
    ConfiguratorProductListVirtual,
  },
  props: {
    isDocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      BLOCK_SELECTOR: 'configurator-editor-theme-picker-modal',
      genericThemeType: ThemeType.GENERIC,
      ThemeType,
      isDualSelectHidden: false,
      lastScrollTop: 0,
    };
  },
  computed: {
    activeCategoryId: {
      get() {
        return this.$store.state.configurator.activeCategoryId;
      },
      set(v) {
        this.$store.dispatch(
          prefixConfiguratorType(ConfiguratorAction.SET_ACTIVE_STATE),
          { categoryId: v, themeType: this.genericThemeType }
        );
      },
    },
    ...mapState({
      themeType: 'themeType',
    }),
    blockClasses() {
      return [
        {
          [this.BLOCK_SELECTOR]: true,
          [`${this.BLOCK_SELECTOR}--is-docked`]: this.isDocked,
        },
      ];
    },
    products() {
      return this.$store.getters[
        prefixGlassesType(GlassesGetter.GET_MATCHING_GENERIC_THEMES)
      ];
    },
    dualSelectClasses() {
      return {
        [`${this.BLOCK_SELECTOR}__dual-select`]: true,
        [`${this.BLOCK_SELECTOR}__dual-select--hidden`]:
          this.isDualSelectHidden && !this.isDocked,
      };
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.attachScrollListener();
    });
  },
  beforeDestroy() {
    this.detachScrollListener();
  },
  methods: {
    getScrollerElement() {
      // Get the internal .vue-recycle-scroller element from ConfiguratorProductListVirtual
      const productListEl = this.$refs.productListVirtual?.$el;
      return productListEl?.querySelector('.vue-recycle-scroller') || null;
    },
    attachScrollListener() {
      const scrollerEl = this.getScrollerElement();
      if (scrollerEl) {
        scrollerEl.addEventListener('scroll', this.handleScroll, {
          passive: true,
        });
      }
    },
    detachScrollListener() {
      const scrollerEl = this.getScrollerElement();
      if (scrollerEl) {
        scrollerEl.removeEventListener('scroll', this.handleScroll);
      }
    },
    handleScroll(event) {
      // Only apply hide behavior on mobile (non-docked mode)
      if (this.isDocked) return;

      const scrollTop = event.target.scrollTop;
      const isScrollingDown = scrollTop > this.lastScrollTop;
      const scrollThreshold = 10;

      if (
        isScrollingDown &&
        scrollTop > scrollThreshold &&
        !this.isDualSelectHidden
      ) {
        const dualSelectEl = this.$refs.dualSelect?.$el;
        const scrollerEl = this.getScrollerElement();
        if (scrollerEl && dualSelectEl) {
          scrollerEl.style.paddingTop = `${dualSelectEl.offsetHeight}px`;
        }
        this.isDualSelectHidden = true;
      } else if (!isScrollingDown && this.isDualSelectHidden) {
        this.isDualSelectHidden = false;
        const scrollerEl = this.getScrollerElement();
        if (scrollerEl) {
          scrollerEl.style.paddingTop = '';
        }
      }

      this.lastScrollTop = scrollTop;
    },
    handleConfirmAction() {
      globalThis.dispatchEvent(new CustomEvent('lgf.configurator.stepVisual'));
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-editor-theme-picker-modal {
  $block-selector: &;

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  bottom: 0;
  overflow: hidden;
  background-color: $color-white;
  display: grid;
  grid-template-rows: min-content 1fr min-content;
  grid-template-columns: 1fr;
  grid-template-areas:
    'header'
    'body'
    'footer';
  z-index: 1000;

  &--is-docked {
    border-radius: 1.875rem;
    position: absolute;
  }

  &__header {
    grid-area: header;
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: $color-arapawa;
    color: $color-white;
    padding: 0.625rem 0.875rem 0.625rem 0.625rem;

    #{$block-selector}--is-docked & {
      padding: 1.25rem 1.875rem;
      align-items: center;
      background-color: transparent;
      color: $color-arapawa;
    }
  }

  &__title-wrapper {
    flex: 1;
  }

  &__title {
    margin: 0;
    font-weight: 600;
    font-size: 1.125rem;
    text-transform: uppercase;
  }

  &__sub-title {
    margin: 0;
    font-weight: 600;
    font-size: 1rem;
  }

  &__close-button {
    background: none;
    border: none;
    cursor: pointer;

    &::before {
      @extend %icon;
      @extend %icon--close;

      font-size: 1rem;
      color: $color-white;
    }

    &:hover {
      opacity: 0.7;
    }

    @include breakpoint($breakpoint-l) {
      &::before {
        color: $color-arapawa;
      }
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
  }

  &__dual-select {
    flex-shrink: 0;
    transition: transform 0.3s ease, opacity 0.3s ease, max-height 0.3s ease;
    max-height: 10rem;
    overflow: hidden;
    padding: 0.625rem;

    &--hidden {
      transform: translateY(-100%);
      opacity: 0;
      max-height: 0;
      pointer-events: none;
      padding: 0 0.625rem;
    }

    @include breakpoint($breakpoint-l) {
      // Disable hide behavior on desktop
      &--hidden {
        transform: none;
        opacity: 1;
        max-height: 10rem;
        pointer-events: auto;
      }
    }
  }

  &__product-list-virtual {
    flex: 1;
    overflow-y: auto;

    ::v-deep .vue-recycle-scroller {
      transition: padding-top 0.3s ease;
    }
  }

  &__footer {
    display: block;
    grid-area: footer;
    height: 4.6875rem;
    background-color: $color-white;
    box-shadow: 0 -0.25rem 0.25rem 0 rgba(0, 0, 0, 0.1);
    border-radius: 1.5625rem;
  }

  &__footer-toolbar {
    margin: 0;
    height: 100%;
    align-items: center;

    & > * {
      margin: 0;
    }
  }

  &__button-confirm {
    height: 2.75rem;
    font-size: 1rem;
    font-weight: 700;

    &::after {
      @extend %icon;
      @extend %icon--arrow-button;

      font-size: 1.125rem;
    }
  }
}
</style>
